import React, { useState } from 'react'
import { Grid, Box, IconButton, CircularProgress, Tooltip, InputAdornment } from '@mui/material'
import { get } from 'lodash'
import CustomTextField from '../../../../components/CustomTextField/CustomTextField'
import useOrganizationDetailsStyles from './styles'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import RefreshIcon from '@mui/icons-material/Refresh'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { fetchZohoOrganizationData } from '../../../../services/organization.service'

const OrganizationDetails = ({ formik }) => {
  const classes = useOrganizationDetailsStyles()
  const [loading, setLoading] = useState(false)
  const [fillStatus, setFillStatus] = useState(null) // 'idle', 'loading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('')

  // Mapping from Zoho API response to form field names
  const mapZohoDataToForm = (zohoData) => {
    const org = zohoData?.organization || {}
    const mappedData = {
      organizationName: org.name || '',
      organizationCode:
        formik.values.organizationDetails?.organizationCode || '', // Keep existing code
      email: org.email || '',
      phone: org.phone || '',
      website: org.website || '',
      address: org.address?.formatted_address || '',
      gstNumber: org.gstin || org.gst_number || '',
      description: '',
    }
    return mappedData
  }

  // Fetch Zoho data and auto-fill
  const handleAutoFill = async () => {
    const orgCode = formik.values.organizationDetails?.organizationCode

    if (!orgCode || orgCode.trim() === '') {
      setErrorMessage('Please enter Organization Code/ID first')
      setFillStatus('error')
      setTimeout(() => setFillStatus(null), 3000)
      return
    }

    try {
      setLoading(true)
      setFillStatus('loading')
      setErrorMessage('')

      // Fetch data from the Zoho API using the service
      const result = await fetchZohoOrganizationData(orgCode)

      if (!result.success || !result.data) {
        throw new Error(result.error || 'No data found for this organization ID')
      }

      // Map and fill the form with available data
      const mappedData = mapZohoDataToForm(result.data)

      // Only fill fields that have data
      Object.entries(mappedData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          formik.setFieldValue(`organizationDetails.${key}`, value)
        }
      })

      setFillStatus('success')
      setTimeout(() => setFillStatus(null), 3000)
    } catch (error) {
      console.error('Error fetching organization data:', error)
      setErrorMessage(error.message || 'Failed to fetch organization data')
      setFillStatus('error')
      setTimeout(() => setFillStatus(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  const getButtonIcon = () => {
    if (fillStatus === 'loading') {
      return <CircularProgress size={20} />
    } else if (fillStatus === 'success') {
      return <AutoFixHighIcon />
    } else if (fillStatus === 'error') {
      return <ErrorOutlineIcon />
    }
    return <AutoFixHighIcon />
  }

  const getButtonColor = () => {
    if (fillStatus === 'success') return 'success'
    if (fillStatus === 'error') return 'error'
    if (fillStatus === 'loading') return 'inherit'
    return 'primary'
  }

  const getTooltipTitle = () => {
    if (fillStatus === 'loading') return 'Fetching data...'
    if (fillStatus === 'success') return 'Data filled successfully!'
    if (fillStatus === 'error')
      return errorMessage || 'Error fetching data. Click to retry'
    return 'Auto-fill organization details from Zoho'
  }

  return (
    <div className={classes.root}>
      <Box
        sx={{
          // remove default margin on Mui TextField to avoid extra gaps between grid columns
          '& .MuiTextField-root': { m: 0, width: '100%' },
        }}
        noValidate
        autoComplete="off"
      >
        <Grid container spacing={2} className="mb_20">
          <Grid item xs={12} md={4}>
            <div className=" textfield " style={{ margin: '0px' }}>
              <CustomTextField
                label="Organization Code/ID"
                id="filled-size-small"
                fullWidth
                value={
                  formik.values.organizationDetails?.organizationCode || ''
                }
                onChange={(value) => {
                  formik.setFieldValue(
                    'organizationDetails.organizationCode',
                    value.target.value
                  )
                  setFillStatus(null)
                }}
                error={formik.errors?.organizationDetails?.organizationCode}
                helperText={
                  get(formik, 'touched.organizationDetails.organizationCode') &&
                  get(formik, 'errors.organizationDetails.organizationCode')
                }
                // allow flex item to shrink correctly inside Grid by setting minWidth
                style={{ flex: '1 1 auto', minWidth: 0, margin: '0px' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ height: '100%' }}>
                      <Tooltip title={getTooltipTitle()}>
                        <IconButton
                          onClick={handleAutoFill}
                          disabled={loading}
                          color={getButtonColor()}
                          size="small"
                          disableRipple
                          sx={{
                            height: '36px',
                            padding: '6px',
                            borderRadius: '0 6px 6px 0',
                            minWidth: '40px',
                            borderLeft: `1px solid rgba(0,0,0,0.23)`,
                            backgroundColor:
                              fillStatus === 'success'
                                ? '#e8f5e9'
                                : fillStatus === 'error'
                                  ? '#ffebee'
                                  : 'transparent',
                            '& svg': { fontSize: '20px' },
                            '&:hover': {
                              backgroundColor:
                                fillStatus === 'success'
                                  ? '#c8e6c9'
                                  : fillStatus === 'error'
                                    ? '#ffcdd2'
                                    : 'rgba(0,0,0,0.04)',
                              transform: 'scale(1.02)',
                            },
                          }}
                        >
                          {getButtonIcon()}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className=" textfield ">
              <CustomTextField
                label="Organization Name"
                id="filled-size-small"
                value={
                  formik.values.organizationDetails?.organizationName || ''
                }
                onChange={(value) =>
                  formik.setFieldValue(
                    'organizationDetails.organizationName',
                    value.target.value
                  )
                }
                error={formik.errors?.organizationDetails?.organizationName}
                helperText={
                  get(formik, 'touched.organizationDetails.organizationName') &&
                  get(formik, 'errors.organizationDetails.organizationName')
                }
              />
            </div>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className=" textfield ">
              <CustomTextField
                label="Email"
                id="filled-size-small"
                type="email"
                value={formik.values.organizationDetails?.email || ''}
                onChange={(value) =>
                  formik.setFieldValue(
                    'organizationDetails.email',
                    value.target.value
                  )
                }
                error={formik.errors?.organizationDetails?.email}
                helperText={
                  get(formik, 'touched.organizationDetails.email') &&
                  get(formik, 'errors.organizationDetails.email')
                }
              />
            </div>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className=" textfield ">
              <CustomTextField
                label="Phone"
                id="filled-size-small"
                value={formik.values.organizationDetails?.phone || ''}
                onChange={(value) =>
                  formik.setFieldValue(
                    'organizationDetails.phone',
                    value.target.value
                  )
                }
                error={formik.errors?.organizationDetails?.phone}
                helperText={
                  get(formik, 'touched.organizationDetails.phone') &&
                  get(formik, 'errors.organizationDetails.phone')
                }
              />
            </div>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className=" textfield ">
              <CustomTextField
                label="Website"
                id="filled-size-small"
                value={formik.values.organizationDetails?.website || ''}
                onChange={(value) =>
                  formik.setFieldValue(
                    'organizationDetails.website',
                    value.target.value
                  )
                }
                error={formik.errors?.organizationDetails?.website}
                helperText={
                  get(formik, 'touched.organizationDetails.website') &&
                  get(formik, 'errors.organizationDetails.website')
                }
              />
            </div>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className=" textfield ">
              <CustomTextField
                label="Address"
                id="filled-size-small"
                value={formik.values.organizationDetails?.address || ''}
                onChange={(value) =>
                  formik.setFieldValue(
                    'organizationDetails.address',
                    value.target.value
                  )
                }
                error={formik.errors?.organizationDetails?.address}
                helperText={
                  get(formik, 'touched.organizationDetails.address') &&
                  get(formik, 'errors.organizationDetails.address')
                }
              />
            </div>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className=" textfield ">
              <CustomTextField
                label="GST Number"
                id="filled-size-small"
                value={formik.values.organizationDetails?.gstNumber || ''}
                onChange={(value) =>
                  formik.setFieldValue(
                    'organizationDetails.gstNumber',
                    value.target.value
                  )
                }
                error={formik.errors?.organizationDetails?.gstNumber}
                helperText={
                  get(formik, 'touched.organizationDetails.gstNumber') &&
                  get(formik, 'errors.organizationDetails.gstNumber')
                }
              />
            </div>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className=" textfield ">
              <CustomTextField
                label="Description"
                id="filled-size-small"
                multiline
                rows={4}
                value={formik.values.organizationDetails?.description || ''}
                onChange={(value) =>
                  formik.setFieldValue(
                    'organizationDetails.description',
                    value.target.value
                  )
                }
                error={formik.errors?.organizationDetails?.description}
                helperText={
                  get(formik, 'touched.organizationDetails.description') &&
                  get(formik, 'errors.organizationDetails.description')
                }
              />
            </div>
          </Grid>
        </Grid>

        <div style={{ marginTop: '20px' }}>
          <Grid container spacing={2} className="mb_20">
            <Grid item xs={12} md={4}>
              <div className=" textfield ">
                <CustomTextField
                  label="Bank Name"
                  id="filled-size-small"
                  value={
                    formik.values.organizationDetails?.billerDetails
                      ?.nameOfTheBank || ''
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'organizationDetails.billerDetails.nameOfTheBank',
                      value.target.value
                    )
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className=" textfield ">
                <CustomTextField
                  label="IFSC Code"
                  id="filled-size-small"
                  value={
                    formik.values.organizationDetails?.billerDetails
                      ?.ifscCode || ''
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'organizationDetails.billerDetails.ifscCode',
                      value.target.value
                    )
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className=" textfield ">
                <CustomTextField
                  label="Bank Address"
                  id="filled-size-small"
                  value={
                    formik.values.organizationDetails?.billerDetails
                      ?.addressOfTheBank || ''
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'organizationDetails.billerDetails.addressOfTheBank',
                      value.target.value
                    )
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className=" textfield ">
                <CustomTextField
                  label="Entity Name"
                  id="filled-size-small"
                  value={
                    formik.values.organizationDetails?.billerDetails
                      ?.entityName || ''
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'organizationDetails.billerDetails.entityName',
                      value.target.value
                    )
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className=" textfield ">
                <CustomTextField
                  label="Account Number"
                  id="filled-size-small"
                  value={
                    formik.values.organizationDetails?.billerDetails
                      ?.accountNumber || ''
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'organizationDetails.billerDetails.accountNumber',
                      value.target.value
                    )
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className=" textfield ">
                <CustomTextField
                  label="Bill To Address"
                  id="filled-size-small"
                  value={
                    formik.values.organizationDetails?.billerDetails
                      ?.billToAddress || ''
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'organizationDetails.billerDetails.billToAddress',
                      value.target.value
                    )
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className=" textfield ">
                <CustomTextField
                  label="Entity Address"
                  id="filled-size-small"
                  multiline
                  rows={2}
                  value={
                    formik.values.organizationDetails?.billerDetails
                      ?.entityAddress || ''
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'organizationDetails.billerDetails.entityAddress',
                      value.target.value
                    )
                  }
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </Box>
    </div>
  )
}

OrganizationDetails.propTypes = {}

export default OrganizationDetails
